import { Stack } from "@mui/material";
import { FormEvent, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchNearbyPharmaciesQuery } from "src/api/services/e-prescribeApi";
import {
  useAddPreferredPharmacyMutation,
  useGetPatientPreferredPharmacyQuery,
} from "src/api/services/preferredPharmacyApi";
import { EPrescribePharmacy } from "src/api/types";
import LoadingButton from "src/components/Buttons/LoadingButton";
import PharmacyAutocomplete from "src/components/PharmacyAutocomplete";
import { useSentryLogger } from "src/hooks/useSentryLogger";
import { useSubmitAnswers } from "src/hooks/useSubmitAnswers";
import { formattedPharmacyAddress } from "src/utils/formatPharmacyAddress";

const PharmacySearch = () => {
  const [addPharmacy] = useAddPreferredPharmacyMutation();
  const { data: patientPharmacy } = useGetPatientPreferredPharmacyQuery();
  const { submitAnswer } = useSubmitAnswers();
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedPharmacy, setSelectedPharmacy] =
    useState<EPrescribePharmacy | null>(null);

  const { data = [] } = useSearchNearbyPharmaciesQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const errorLog = useSentryLogger("error");
  const { t } = useTranslation();

  const next = useCallback(() => {
    submitAnswer(["Ignore"]);
    setSelectedPharmacy(null);
  }, [submitAnswer]);

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      if (selectedPharmacy == null) return null;

      e.preventDefault();
      setLoading(true);

      try {
        const pharmacy = await addPharmacy({
          external_pharmacy_id: String(selectedPharmacy.PharmacyId),
          name: selectedPharmacy.StoreName,
          full_address: formattedPharmacyAddress(selectedPharmacy),
        }).unwrap();

        if (pharmacy != null) {
          next();
        }
      } catch (err) {
        errorLog("Could not add preferred pharmacy", err);
      } finally {
        setLoading(false);
      }
    },
    [addPharmacy, errorLog, next, selectedPharmacy],
  );

  useEffect(() => {
    if (patientPharmacy == null) return;

    const matchedPharmacy =
      data.find(
        (pharmacy) =>
          pharmacy.PharmacyId === Number(patientPharmacy.external_pharmacy_id),
      ) ?? null;
    setSelectedPharmacy(matchedPharmacy);
  }, [data, patientPharmacy]);

  return (
    <Stack component="form" role="form" onSubmit={handleSubmit} gap={3}>
      <PharmacyAutocomplete
        patientPharmacy={patientPharmacy}
        selectedPharmacy={selectedPharmacy}
        setSelectedPharmacy={setSelectedPharmacy}
      />
      <LoadingButton
        loading={loading}
        type="submit"
        disabled={!selectedPharmacy}
      >
        {t(`button.next`)}
      </LoadingButton>
    </Stack>
  );
};

export default PharmacySearch;
